// src/modules/app-views/products/pages/Document/tabs/AiInfoTab/InfoproductDataSection.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { Flex, Descriptions, Collapse, Tag, Typography, Rate, theme } from 'antd';
import {
  ReadOutlined,
  OrderedListOutlined,
} from '@ant-design/icons';
import translateX from 'utils/translateX';

const { Text } = Typography;

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const InfoproductDataSection = ({ data, isMobile }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { token } = theme.useToken();
  const info = data?.infoproductData;

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. LOCAL VARIABLES
  // ─────────────────────────────────────────────────────────────────────────────
  const hasData = info && (info.instructor || info.format || info.durationHours || info.platform || info.curriculum?.length);

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. UI
  // ─────────────────────────────────────────────────────────────────────────────
  if (!hasData) return null;

  return (
    <>
      {/* MAIN INFO */}
      <Collapse
        className="collapsed-content-fit"
        collapsible="icon"
        defaultActiveKey={['infoproduct-info']}
        style={{ marginBottom: 16 }}
        items={[
          {
            key: 'infoproduct-info',
            label: (
              <Flex align="center" gap={8}>
                <ReadOutlined style={{ color: token.colorPrimary }} />
                <span style={{ fontWeight: 600 }}>{translateX('infoproduct_info')}</span>
              </Flex>
            ),
            showArrow: false,
            children: (
              <Descriptions
                bordered
                size="small"
                column={1}
                layout={isMobile ? 'vertical' : 'horizontal'}
                styles={{ label: { fontWeight: 500 } }}
              >
                {info.instructor && (
                  <Descriptions.Item label={translateX('instructor')}>
                    {info.instructor}
                  </Descriptions.Item>
                )}
                {info.instructorBio && (
                  <Descriptions.Item label={translateX('instructor_bio')}>
                    <Text type="secondary" style={{ fontSize: 13 }}>{info.instructorBio}</Text>
                  </Descriptions.Item>
                )}
                {info.format && (
                  <Descriptions.Item label={translateX('format')}>
                    <Tag color="blue">{info.format}</Tag>
                  </Descriptions.Item>
                )}
                {info.level && (
                  <Descriptions.Item label={translateX('level')}>
                    <Tag color={info.level === 'advanced' ? 'red' : info.level === 'intermediate' ? 'orange' : 'green'}>
                      {info.level}
                    </Tag>
                  </Descriptions.Item>
                )}
                {info.durationHours !== undefined && info.durationHours !== null && (
                  <Descriptions.Item label={translateX('duration_hours')}>
                    {info.durationHours}h
                  </Descriptions.Item>
                )}
                {info.language && (
                  <Descriptions.Item label={translateX('language')}>
                    {info.language}
                  </Descriptions.Item>
                )}
                {info.platform && (
                  <Descriptions.Item label={translateX('platform')}>
                    <Tag>{info.platform}</Tag>
                  </Descriptions.Item>
                )}
                {info.accessType && (
                  <Descriptions.Item label={translateX('access_type')}>
                    <Tag color={info.accessType === 'lifetime' ? 'green' : 'blue'}>{info.accessType}</Tag>
                  </Descriptions.Item>
                )}
                {info.accessDurationMonths !== undefined && info.accessDurationMonths !== null && (
                  <Descriptions.Item label={translateX('access_duration')}>
                    {info.accessDurationMonths} {translateX('months')}
                  </Descriptions.Item>
                )}
                {info.hasCertificate !== undefined && (
                  <Descriptions.Item label={translateX('has_certificate')}>
                    {info.hasCertificate ? (
                      <Tag color="green">{translateX('yes')}</Tag>
                    ) : (
                      <Tag color="red">{translateX('no')}</Tag>
                    )}
                  </Descriptions.Item>
                )}
                {info.certificateType && (
                  <Descriptions.Item label={translateX('certificate_type')}>
                    <Tag>{info.certificateType}</Tag>
                  </Descriptions.Item>
                )}
                {info.studentsCount !== undefined && info.studentsCount !== null && (
                  <Descriptions.Item label={translateX('students_count')}>
                    {info.studentsCount.toLocaleString('pt-BR')}
                  </Descriptions.Item>
                )}
                {info.rating !== undefined && info.rating !== null && (
                  <Descriptions.Item label={translateX('rating')}>
                    <Flex align="center" gap={8}>
                      <Rate disabled allowHalf value={info.rating} style={{ fontSize: 14 }} />
                      <Text type="secondary">({info.rating})</Text>
                    </Flex>
                  </Descriptions.Item>
                )}
                {info.targetAudience && (
                  <Descriptions.Item label={translateX('target_audience')}>
                    <Text type="secondary" style={{ fontSize: 13 }}>{info.targetAudience}</Text>
                  </Descriptions.Item>
                )}
                {info.productUrl && (
                  <Descriptions.Item label={translateX('product_url')}>
                    <a href={info.productUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12 }}>
                      {info.productUrl}
                    </a>
                  </Descriptions.Item>
                )}
                {info.includes?.length > 0 && (
                  <Descriptions.Item label={translateX('includes')}>
                    <Flex wrap="wrap" gap={4}>
                      {info.includes.map((item, i) => (
                        <Tag key={i} color="green" style={{ margin: 0 }}>{item}</Tag>
                      ))}
                    </Flex>
                  </Descriptions.Item>
                )}
                {info.prerequisites?.length > 0 && (
                  <Descriptions.Item label={translateX('prerequisites')}>
                    <Flex wrap="wrap" gap={4}>
                      {info.prerequisites.map((prereq, i) => (
                        <Tag key={i} style={{ margin: 0 }}>{prereq}</Tag>
                      ))}
                    </Flex>
                  </Descriptions.Item>
                )}
              </Descriptions>
            ),
          },
        ]}
      />

      {/* CURRICULUM */}
      {info.curriculum?.length > 0 && (
        <Collapse
          className="collapsed-content-fit"
          collapsible="icon"
          defaultActiveKey={['curriculum']}
          style={{ marginBottom: 16 }}
          items={[
            {
              key: 'curriculum',
              label: (
                <Flex align="center" gap={8}>
                  <OrderedListOutlined style={{ color: token.colorPrimary }} />
                  <span style={{ fontWeight: 600 }}>{translateX('curriculum')}</span>
                  <Tag>{info.curriculum.length}</Tag>
                </Flex>
              ),
              showArrow: false,
              children: (
                <div style={{ padding: 16 }}>
                  {info.curriculum.map((item, i) => (
                    <div
                      key={i}
                      style={{
                        marginBottom: i < info.curriculum.length - 1 ? 12 : 0,
                        padding: '10px 12px',
                        borderRadius: 8,
                        backgroundColor: 'var(--ant-color-fill-quaternary)',
                      }}
                    >
                      <Flex align="center" gap={8}>
                        {item.module && <Tag color="blue" style={{ margin: 0 }}>{item.module}</Tag>}
                        <Text strong style={{ fontSize: 13 }}>{item.title}</Text>
                        {item.durationMinutes && (
                          <Tag style={{ margin: 0 }}>{item.durationMinutes} min</Tag>
                        )}
                      </Flex>
                      {item.description && (
                        <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
                          {item.description}
                        </Text>
                      )}
                    </div>
                  ))}
                </div>
              ),
            },
          ]}
        />
      )}
    </>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default InfoproductDataSection;
